import { useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLang, errorText } from "@/lib/i18n";
import { fmtRange } from "@/lib/format";

const FlowDialog = ({ prompt, onClose }) => {
  const { t } = useLang();
  const conflict = prompt?.kind === "conflict";
  return (
    <AlertDialog open={!!prompt} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="rounded-2xl" data-testid={conflict ? "conflict-dialog" : "override-dialog"}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">{t(conflict ? "b.forceTitle" : "b.override")}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              <strong>{prompt?.name}</strong> {t(conflict ? "b.forceText" : "b.overrideText")}
              {conflict && (
                <ul className="mt-3 space-y-1" data-testid="conflict-list">
                  {prompt.conflicts.map((c, i) => <li key={i} className="text-sm"><span className="font-medium">{c.event_name}</span> <span className="mono text-xs">{fmtRange(c.start, c.end, t)}</span></li>)}
                </ul>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={conflict ? "conflict-cancel" : "override-cancel"}>{t("c.cancel")}</AlertDialogCancel>
          <AlertDialogAction className="rounded-full bg-[#C5A059] text-[#0A0A0C] hover:bg-[#D9B872]" data-testid={conflict ? "conflict-force" : "override-confirm"}
            onClick={() => { const next = prompt.next; onClose(); next(); }}>{t(conflict ? "b.forceBtn" : "b.overrideBtn")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Runs an assignment request; asks to override qualification or force a schedule conflict when needed.
export const useAssignFlow = (reload) => {
  const { t } = useLang();
  const [prompt, setPrompt] = useState(null);
  const run = async (req, okMsg, name, flags = { override: false, force: false }) => {
    try {
      await req(flags);
      toast.success(okMsg);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const retry = (extra) => () => run(req, okMsg, name, { ...flags, ...extra });
      if (d?.code === "not_qualified") setPrompt({ kind: "override", name, next: retry({ override: true }) });
      else if (d?.code === "schedule_conflict") setPrompt({ kind: "conflict", name, conflicts: d.conflicts || [], next: retry({ force: true }) });
      else toast.error(errorText(t, e));
    }
  };
  return { run, dialog: <FlowDialog prompt={prompt} onClose={() => setPrompt(null)} /> };
};
